import { Box, Text, Image, Icon, TokensIcon } from '@bonanza-social/moonlight';
import { memo } from 'react';
import { State, useSelector, selectTokenIconSmall } from '@bonanza-social/toolkit/state';
import TimeAndProgress from './components/TimeAndProgress/TimeAndProgress';
import QuestButtons from './components/QuestButtons/QuestButtons';
import { selectQuest } from '../../state';
import { hasQuestStarted } from '../../utils/questHasStarted';
import AdditionalInfo from './components/AdditionalInfo';
import { QuestStatus } from '../../services/QuestsService.schemas';
import { QuestItemProps } from './QuestItem.types';
import { useTranslation } from '@bonanza-social/i18n';
import { formatAmount } from '@bonanza-social/moonlight';
import { rewardIconMap } from './QuestItem.content';

function QuestItem({ id, detailsPageView = false }: QuestItemProps) {
  const quest = useSelector((state: State) => selectQuest(state, id));
  const tokenIcon = useSelector(selectTokenIconSmall);
  const { t } = useTranslation('quests');

  if (!quest) {
    return null;
  }

  const {
    imageUrl,
    title,
    promotionId,
    rewards,
    status,
    subTypeMeta,
    subPromoType,
  } = quest;

  const isCompleted = (quest.status as QuestStatus) === QuestStatus.Completed;

  const questHasStarted = hasQuestStarted(subTypeMeta.startDate);

  const [reward] = rewards;

  return (
    <Box
      width={'100%'}
      justifyContent="space-between"
      paddingVertical="space-800"
      height="100%"
      flex={1}
      alignSelf="center"
      {...(detailsPageView
        ? {
            flexDirection: {
              mobile: 'column',
              tablet: 'column',
              desktop: 'row',
            },
            flexWrap: {
              mobile: 'nowrap',
              tablet: 'nowrap',
              desktop: 'wrap',
            },
            gap: {
              mobile: undefined,
              tablet: undefined,
              desktop: 'space-400',
            },
            alignItems: {
              mobile: 'flex-start',
              tablet: 'flex-start',
              desktop: 'center',
            },
            maxWidth: 980,
          }
        : {
            alignItems: 'flex-start',
            maxWidth: {
              mobile: undefined,
              tablet: '49%',
              desktop: '32%',
            },
          })}
    >
      <Image
        source={imageUrl}
        width="100%"
        borderRadius="radius-300"
        aspectRatio={16 / 9}
        opacity={!detailsPageView && isCompleted ? 0.5 : 1}
        {...(detailsPageView
          ? {
              flex: {
                mobile: undefined,
                tablet: undefined,
                desktop: 3.5,
              },
              height: {
                mobile: 163,
                tablet: 308,
                desktop: 308,
              },
            }
          : {})}
      />
      <Box
        gap="space-400"
        marginTop="space-400"
        width={'100%'}
        flexGrow={1}
        {...(detailsPageView
          ? {
              flex: {
                mobile: undefined,
                tablet: undefined,
                desktop: 1.5,
              },
            }
          : {})}
      >
        <Box
          opacity={!detailsPageView && isCompleted ? 0.5 : 1}
          gap="space-400"
          flexGrow={1}
          justifyContent="space-between"
        >
          <Text
            variant="h4"
            {...(detailsPageView
              ? { gradient: 'darkSurfaceAccent', marginBottom: 'space-400' }
              : { color: 'darkSurfacePrimary' })}
          >
            {title}
          </Text>
          <Box gap="space-400">
            <Box flexDirection="row" gap="space-200" alignItems="center">
              <Icon
                name="targetArrow"
                width={20}
                height={20}
                gradient="accentBackground"
              />
              <Text variant="body-m-semi" color="darkSurfacePrimary">
                {subPromoType === 'BetAmount'
                  ? `Bet $${subTypeMeta?.targetAccumulation}`
                  : `Spin ${subTypeMeta?.targetAccumulation} times`}
              </Text>
            </Box>

            <Box flexDirection="row" gap="space-200" alignItems="center">
              {reward.rewardTarget === 'Tokens' ? (
                <TokensIcon {...tokenIcon} width={20} height={20} />
              ) : (
                <Icon
                  name={rewardIconMap[reward.rewardTarget]}
                  width={20}
                  height={20}
                  gradient="accentBackground"
                />
              )}
              <Text variant="body-m-semi" color="darkSurfacePrimary">
                {t(`questReward_${reward.rewardTarget}`, {
                  amount: formatAmount(reward.rewardAmount),
                })}
              </Text>
            </Box>

            {!isCompleted ? (
              <TimeAndProgress
                subPromoType={subPromoType}
                subTypeMeta={subTypeMeta}
                questHasStarted={questHasStarted}
                status={status}
                detailsPageView={detailsPageView}
                id={id}
              />
            ) : null}
          </Box>
        </Box>
        {isCompleted && (
          <Box
            variant="gradient"
            gradient="darkSurfaceBrand"
            borderRadius="radius-600"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            height={36}
            gap="space-200"
          >
            <Image name="common.GreenCheck" width={20} height={20} />
            <Text
              variant="caption-semi"
              locNS="quests"
              locKey="questCompleted"
            />
          </Box>
        )}

        <QuestButtons
          quest={quest}
          questHasStarted={questHasStarted}
          isCompleted={isCompleted}
          detailsPageView={detailsPageView}
        />
      </Box>
      {detailsPageView ? <AdditionalInfo id={promotionId} /> : null}
    </Box>
  );
}
export default memo(QuestItem);
